import React, { useState, useEffect } from 'react';

/**
 * Thermometer Scale - EQ-5D style vertical thermometer.
 *
 * A vertical scale with graduated markings and a colored fill level.
 * Commonly used in quality-of-life assessments (EQ-5D VAS, health utility).
 * The participant taps/clicks on the thermometer to indicate their level.
 *
 * Usage: responseConfig.layout = 'thermometer'
 */
const ThermometerScale = ({
  minValue = 0,
  maxValue = 100,
  step = 10,
  value = null,
  onChange,
  disabled = false,
  topAnchor = 'Best imaginable health state',
  bottomAnchor = 'Worst imaginable health state',
  height = 400,
  width = 60,
  fillColor = '#0d9488',
  trackColor = '#e2e8f0',
  showCurrentValue = true,
  className = '',
}) => {
  const [selectedValue, setSelectedValue] = useState(value ?? minValue);

  // Keep internal state in sync with the value prop (prevents stale display on re-visit/resume)
  useEffect(() => {
    setSelectedValue(value ?? minValue);
  }, [value]);

  const range = maxValue - minValue;
  const ticks = [];
  for (let v = maxValue; v >= minValue; v -= step) {
    ticks.push(v);
  }

  const fillPercent = ((selectedValue - minValue) / range) * 100;

  const handleClick = (e) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const percent = 1 - (clickY / rect.height);
    const rawValue = minValue + (percent * range);
    const snapped = Math.round(rawValue / step) * step;
    const clamped = Math.max(minValue, Math.min(maxValue, snapped));
    setSelectedValue(clamped);
    if (onChange) onChange(clamped);
  };

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        gap: 16,
        height,
        userSelect: 'none',
      }}
    >
      {/* Top anchor */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        width: 120,
        paddingTop: 4,
        paddingBottom: 4,
      }}>
        <span style={{ fontSize: 11, color: '#64748b', textAlign: 'right', fontFamily: 'system-ui, sans-serif', lineHeight: 1.2 }}>
          {topAnchor}
        </span>
        <span style={{ fontSize: 11, color: '#64748b', textAlign: 'right', fontFamily: 'system-ui, sans-serif', lineHeight: 1.2 }}>
          {bottomAnchor}
        </span>
      </div>

      {/* Tick marks */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        width: 30,
        paddingTop: 2,
        paddingBottom: 2,
      }}>
        {ticks.map((v) => (
          <span key={v} style={{
            fontSize: 10,
            fontWeight: v === maxValue || v === minValue || v === Math.round(range / 2 + minValue) ? '700' : '400',
            color: '#475569',
            fontFamily: 'system-ui, sans-serif',
          }}>
            {v}
          </span>
        ))}
      </div>

      {/* Thermometer bar */}
      <div
        onClick={handleClick}
        style={{
          width,
          borderRadius: width / 2,
          backgroundColor: trackColor,
          position: 'relative',
          cursor: disabled ? 'default' : 'pointer',
          border: '2px solid #cbd5e1',
          overflow: 'hidden',
        }}
      >
        {/* Fill */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: `${fillPercent}%`,
          backgroundColor: fillColor,
          borderRadius: `0 0 ${width / 2}px ${width / 2}px`,
          transition: 'height 0.2s ease',
        }} />

        {/* Level indicator */}
        <div style={{
          position: 'absolute',
          bottom: `${fillPercent}%`,
          left: '50%',
          transform: 'translate(-50%, 50%)',
          width: width - 8,
          height: 4,
          backgroundColor: '#1e293b',
          borderRadius: 2,
        }} />
      </div>

      {/* Current value display */}
      {showCurrentValue && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          width: 50,
        }}>
          <div style={{
            position: 'relative',
            top: `${(1 - fillPercent / 100) * 100 - 50}%`,
            backgroundColor: fillColor,
            color: '#fff',
            padding: '4px 10px',
            borderRadius: 6,
            fontSize: 16,
            fontWeight: '700',
            fontFamily: 'system-ui, sans-serif',
            transition: 'top 0.2s ease',
          }}>
            {selectedValue}
          </div>
        </div>
      )}
    </div>
  );
};

export default ThermometerScale;
